import { useCallback, useId } from 'react'
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from '../../design-system/primitives/index.js'
import { selectedPantryCleanupIds } from '../../utils/pantryCleanup.js'
import './PantryCleanupSheet.css'

/**
 * @param {{
 *   open: boolean,
 *   onClose: () => void,
 *   recipeTitle?: string,
 *   rows: import('../../utils/pantryCleanup.js').PantryCleanupRow[],
 *   onRowsChange: (rows: import('../../utils/pantryCleanup.js').PantryCleanupRow[]) => void,
 *   loading?: boolean,
 *   error?: string | null,
 *   submitting?: boolean,
 *   onConfirm: (ids: string[]) => void | Promise<void>,
 * }} props
 */
export default function PantryCleanupSheet({
  open,
  onClose,
  recipeTitle = 'This meal',
  rows,
  onRowsChange,
  loading = false,
  error = null,
  submitting = false,
  onConfirm,
}) {
  const titleId = useId()
  const descId = useId()
  const selectedCount = rows.filter((row) => row.selected).length

  const setAllSelected = useCallback(
    (selected) => {
      onRowsChange(rows.map((row) => ({ ...row, selected })))
    },
    [onRowsChange, rows],
  )

  const keepStaples = useCallback(() => {
    onRowsChange(rows.map((row) => ({ ...row, selected: false })))
  }, [onRowsChange, rows])

  const toggleRow = useCallback(
    (id) => {
      onRowsChange(
        rows.map((row) => (row.id === id ? { ...row, selected: !row.selected } : row)),
      )
    },
    [onRowsChange, rows],
  )

  return (
    <Modal
      open={open}
      onClose={submitting ? undefined : onClose}
      titleId={titleId}
      describedById={descId}
      overlayClassName="pantry-cleanup-overlay"
      contentClassName="pantry-cleanup-sheet"
    >
      <ModalHeader className="pantry-cleanup-header">
        <h2 id={titleId} className="pantry-cleanup-title">
          Update your pantry?
        </h2>
        <Button
          type="button"
          variant="ghost"
          className="modal-close"
          onClick={onClose}
          aria-label="Close"
          disabled={submitting}
        >
          ×
        </Button>
      </ModalHeader>

      <ModalBody className="pantry-cleanup-body">
        <div className="pantry-cleanup-intro">
          <p id={descId} className="pantry-cleanup-subtitle">
            Remove pantry items used to cook this meal. Keep anything you still have.
          </p>
          {recipeTitle ? (
            <p className="pantry-cleanup-recipe" aria-label="Recipe">
              {recipeTitle}
            </p>
          ) : null}
        </div>

        {loading ? (
          <p className="pantry-cleanup-loading" aria-live="polite">
            Loading pantry matches…
          </p>
        ) : rows.length === 0 ? (
          <p className="pantry-cleanup-empty">
            No pantry items matched this recipe. Your pantry was not changed.
          </p>
        ) : (
          <>
            <div className="pantry-cleanup-quick" role="toolbar" aria-label="Selection shortcuts">
              <Button type="button" variant="link" onClick={() => setAllSelected(true)}>
                Select all
              </Button>
              <Button type="button" variant="link" onClick={keepStaples}>
                Keep all
              </Button>
            </div>
            <ul className="pantry-cleanup-list" aria-label="Pantry items to update">
              {rows.map((row) => (
                <li key={row.id}>
                  <label className="pantry-cleanup-row">
                    <input
                      type="checkbox"
                      checked={row.selected}
                      onChange={() => toggleRow(row.id)}
                      disabled={submitting}
                    />
                    <span className="pantry-cleanup-row-name">{row.pantryName || 'Pantry item'}</span>
                  </label>
                </li>
              ))}
            </ul>
          </>
        )}
        {error ? <p className="pantry-cleanup-error" role="alert">{error}</p> : null}
      </ModalBody>

      <ModalFooter className="pantry-cleanup-footer">
        <Button type="button" variant="ghost" onClick={onClose} disabled={submitting}>
          Skip
        </Button>
        <Button
          type="button"
          variant="primary"
          disabled={loading || submitting || selectedCount === 0}
          onClick={() => onConfirm(selectedPantryCleanupIds(rows))}
        >
          {submitting ? 'Updating…' : `Remove selected (${selectedCount})`}
        </Button>
      </ModalFooter>
    </Modal>
  )
}
